import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useCallback } from "react";
import type {
  UseRegisterNewFormProps,
  NewFormData,
  MaintenanceNewFormData,
  PoolRegulationNewFormData,
  AdverseEventNewFormData,
} from "./RegisterNewForm.types";
import { createNewFormSchema } from "./RegisterNewForm.types";
import type { ReportType } from "../../../../api/client/types/catalog.types";
import {
  REPORT_TYPE_NAMES,
  findReportTypeByName,
  isAdverseEventTypeValue,
  extractAdverseEventTypeId,
  isMaintenanceTypeValue,
  extractMaintenanceTypeId,
} from "../../utils/reportTypeConstants";
import { useReportTypeLogic } from "./useReportTypeLogic";
import { colombiaDateTimeToUTCISO } from "../../../../shared/utils/dateUtils";

const isAbsoluteIso = (value: string): boolean => /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);

interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

/**
 * Calendar day for a stored event timestamp.
 * Wall values ("YYYY-MM-DDTHH:mm", or "YYYY-MM-DDT" while the time is still empty)
 * keep their date part. Absolute ISO values are converted to local time, same as before.
 * Falls back to today when there is no usable date.
 */
const calendarPartsFromDateTime = (dateTime?: string): CalendarParts => {
  const today = new Date();
  const fallback: CalendarParts = {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate(),
  };
  if (!dateTime) return fallback;

  const trimmed = dateTime.trim();
  if (isAbsoluteIso(trimmed)) {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return fallback;
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (!match) return fallback;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
};

const formatCalendarParts = ({ year, month, day }: CalendarParts): string => {
  const monthText = String(month).padStart(2, "0");
  const dayText = String(day).padStart(2, "0");
  return `${year}-${monthText}-${dayText}`;
};

/**
 * Combines a date (from dateTime or today) with selected time to create a full datetime string.
 * When editing an existing new, preserves the original event date; otherwise uses current date.
 * An empty time keeps the date and stores "YYYY-MM-DDT" so the field stays invalid until both
 * hour and minute are chosen.
 */
const combineDateWithTime = (dateTime: string | undefined, time: string): string => {
  return `${formatCalendarParts(calendarPartsFromDateTime(dateTime))}T${time}`;
};

/**
 * Extracts time (HH:mm) from a datetime string, or returns empty string if not provided.
 */
const getTimeFromDateTime = (dateTime?: string): string => {
  if (!dateTime) return "";
  const trimmed = dateTime.trim();
  if (isAbsoluteIso(trimmed)) {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return "";
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }
  const match = /T(\d{2}):(\d{2})/.exec(trimmed);
  return match ? `${match[1]}:${match[2]}` : "";
};

/**
 * Formats a datetime string for display (e.g. "7 de febrero de 2026").
 * Falls back to today when no dateTime is provided.
 */
const getDateDisplayFromDateTime = (dateTime?: string): string => {
  const { year, month, day } = calendarPartsFromDateTime(dateTime);
  return new Date(year, month - 1, day).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Extracts date in YYYY-MM-DD format for date input. Falls back to today when not provided or invalid.
 */
const getDateValueFromDateTime = (dateTime?: string): string => {
  return formatCalendarParts(calendarPartsFromDateTime(dateTime));
};

/**
 * Main form hook that handles form state management and data transformation.
 * 
 * Responsibilities:
 * - Form initialization and state management
 * - Form submission and validation
 * - DateTime handling and transformation
 * - Data transformation for API
 * 
 * Business logic is delegated to useReportTypeLogic hook.
 */
export const useRegisterNewForm = ({
  onSubmit,
  reportTypes,
  maintenanceTypes = [],
  interventionLevels = [],
  adverseEventTypes = [],
  initialData,
  swimmer,
}: UseRegisterNewFormProps) => {
  // ============================================
  // FORM INITIALIZATION
  // ============================================

  // When editing a legacy record (no event_specific_factor_id in initialData), relax the
  // factor requirement so old accidents/incidents can still be updated without filling them.
  // New records always require both factor fields for Accidente/Incidente.
  const requireAdverseEventFactors = useMemo(() => {
    if (!initialData) return true;
    const hasFactorData =
      "event_specific_factor_id" in initialData && !!initialData.event_specific_factor_id;
    return hasFactorData;
  }, [initialData]);

  // Create schema with conditional validation based on reportTypes, adverseEventTypes, and interventionLevels
  const schema = useMemo(
    () => createNewFormSchema(reportTypes, adverseEventTypes, interventionLevels, requireAdverseEventFactors),
    [reportTypes, adverseEventTypes, interventionLevels, requireAdverseEventFactors]
  );

  // Extract initial time from datetime (empty string if not provided, forcing manual selection)
  const initialTime = useMemo(
    () => getTimeFromDateTime(initialData?.event_timestamp),
    [initialData?.event_timestamp]
  );

  const form = useForm<
    | NewFormData
    | MaintenanceNewFormData
    | PoolRegulationNewFormData
    | AdverseEventNewFormData
  >({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      event_timestamp:
        initialData?.event_timestamp || (initialTime ? combineDateWithTime(undefined, initialTime) : ""),
      report_type_id: initialData?.report_type_id || "",
      event_description: initialData?.event_description || "",
      security_officer_name: initialData?.security_officer_name || "",
      maintenance_type_id:
        (initialData && "maintenance_type_id" in initialData
          ? initialData.maintenance_type_id
          : "") || "",
      pool_regulation_id:
        (initialData && "pool_regulation_id" in initialData
          ? initialData.pool_regulation_id
          : "") || "",
      intervention_level_id:
        (initialData && "intervention_level_id" in initialData
          ? initialData.intervention_level_id
          : "") || "",
      adverse_events_type_id:
        (initialData && "adverse_events_type_id" in initialData
          ? initialData.adverse_events_type_id
          : "") || "",
      spent_items:
        (initialData && "spent_items" in initialData
          ? initialData.spent_items
          : null) || null,
      event_main_factor_id:
        (initialData && "event_main_factor_id" in initialData
          ? initialData.event_main_factor_id
          : undefined) || undefined,
      event_specific_factor_id:
        (initialData && "event_specific_factor_id" in initialData
          ? initialData.event_specific_factor_id
          : undefined) || undefined,
      adverse_event_intervention_level_id:
        (initialData && "adverse_event_intervention_level_id" in initialData
          ? initialData.adverse_event_intervention_level_id
          : undefined) || undefined,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = form;

  // ============================================
  // BUSINESS LOGIC (Delegated to useReportTypeLogic)
  // ============================================

  const reportTypeLogic = useReportTypeLogic({
    reportTypes,
    maintenanceTypes,
    interventionLevels,
    adverseEventTypes,
    swimmer,
    watch,
  });

  // ============================================
  // DATETIME HANDLING
  // ============================================

  // Watch the time input (we'll use a separate field name for internal handling)
  const eventTime = watch("event_timestamp");

  // Handle time change - preserve existing event date when editing, otherwise use current date
  const handleTimeChange = useCallback(
    (time: string) => {
      const fullDateTime = combineDateWithTime(eventTime, time);
      setValue("event_timestamp", fullDateTime, { shouldValidate: true });
    },
    [setValue, eventTime]
  );

  // Handle date change - preserve current time, update only the date
  const handleDateChange = useCallback(
    (date: string) => {
      const time = getTimeFromDateTime(eventTime);
      const fullDateTime = time ? `${date}T${time}` : `${date}T00:00`;
      setValue("event_timestamp", fullDateTime, { shouldValidate: true });
    },
    [setValue, eventTime]
  );

  // Get time value for the time input (extracted from datetime)
  const currentTimeValue = useMemo(() => {
    return getTimeFromDateTime(eventTime);
  }, [eventTime]);

  // Date display: use event date when available (edit mode), otherwise today (create mode)
  const currentDateDisplay = useMemo(() => {
    return getDateDisplayFromDateTime(eventTime);
  }, [eventTime]);

  // Date value in YYYY-MM-DD for editable date input (allows admin to edit both date and time)
  const currentDateValue = useMemo(() => {
    return getDateValueFromDateTime(eventTime);
  }, [eventTime]);

  // ============================================
  // FORM SUBMISSION & DATA TRANSFORMATION
  // ============================================

  const onFormSubmit = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData
  ) => {
    // Step 1: Convert datetime to UTC ISO format (Colombia → UTC)
    const finalDateTime = colombiaDateTimeToUTCISO(data.event_timestamp);

    // Step 2: Transform maintenance type selections (e.g., "Elementos de Emergencia")
    // In the UI, some maintenance types appear as report type options with special prefixed IDs
    // We need to transform these back to the proper structure for the backend
    let transformedData = transformMaintenanceTypeData(
      {
        ...data,
        event_timestamp: finalDateTime,
      },
      reportTypes
    );

    // Step 3: Transform adverse event type selections
    // In the UI, adverse event types appear as report type options with special prefixed IDs
    // We need to transform these back to the proper structure for the backend
    transformedData = transformAdverseEventData(transformedData, reportTypes);

    onSubmit(transformedData);
  };

  /**
   * Transforms form data when a maintenance type is selected (e.g., "Elementos de Emergencia").
   * Converts: report_type_id="maintenance_1" → report_type_id="1", maintenance_type_id="1"
   * Where "1" is the ID of the "Mantenimiento" report type.
   */
  const transformMaintenanceTypeData = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData,
    reportTypes: ReportType[]
  ): typeof data => {
    // Check if this is a maintenance type selection
    if (!isMaintenanceTypeValue(data.report_type_id)) {
      return data; // No transformation needed
    }

    // Extract the maintenance type ID
    const maintenanceTypeId = extractMaintenanceTypeId(data.report_type_id);

    // Find the "Mantenimiento" report type
    const maintenanceReportType = findReportTypeByName(
      reportTypes,
      REPORT_TYPE_NAMES.MAINTENANCE
    );

    if (!maintenanceReportType) {
      console.error(
        'Could not find "Mantenimiento" report type for transformation'
      );
      return data; // Return original data if transformation fails
    }

    // Transform the data structure
    return {
      ...data,
      report_type_id: maintenanceReportType.id.toString(),
      maintenance_type_id: maintenanceTypeId,
    };
  };

  /**
   * Transforms form data when an adverse event type is selected.
   * Converts: report_type_id="adverse_3" → report_type_id="4", adverse_events_type_id="3"
   * Where "4" is the ID of the "Eventos Adversos" report type.
   */
  const transformAdverseEventData = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData,
    reportTypes: ReportType[]
  ): typeof data => {
    // Check if this is an adverse event type selection
    if (!isAdverseEventTypeValue(data.report_type_id)) {
      return data; // No transformation needed
    }

    // Extract the adverse event type ID
    const adverseEventTypeId = extractAdverseEventTypeId(data.report_type_id);

    // Find the "Eventos Adversos" report type
    const adverseEventsReportType = findReportTypeByName(
      reportTypes,
      REPORT_TYPE_NAMES.ADVERSE_EVENTS
    );

    if (!adverseEventsReportType) {
      console.error(
        'Could not find "Eventos Adversos" report type for transformation'
      );
      return data; // Return original data if transformation fails
    }

    // Transform the data structure
    return {
      ...data,
      report_type_id: adverseEventsReportType.id.toString(),
      adverse_events_type_id: adverseEventTypeId,
    };
  };


  // ============================================
  // RETURN COMBINED VALUES
  // ============================================

  return {
    // Form state
    register,
    setValue,
    handleSubmit: handleSubmit(onFormSubmit),
    control: form.control,
    errors,
    isValid,
    form,
    watch,
    
    // DateTime handling
    currentTimeValue,
    currentDateDisplay,
    currentDateValue,
    handleTimeChange,
    handleDateChange,
    
    // Business logic (from useReportTypeLogic)
    ...reportTypeLogic,
  };
};
